/**
 * Pure join/derive helpers for the merged Plan-vs-Actual "Days" grid (S13).
 *
 * One `DayRow` per calendar day is the union of a schedule entry (the plan) and
 * an optional tracking entry (the actual), joined by org-zone calendar date. The
 * grid component and its tests share this single implementation, and the closure
 * checklist reuses the missing-actual logic.
 *
 * Nothing here touches React or the network — it is deterministic and unit-tested
 * in `__tests__/dayRow.test.ts`.
 */

import { getISOWeek, getISOWeekYear, format, isValid, parseISO } from 'date-fns';
import type { Locale } from 'date-fns/locale';
import { toOrgDateOnly } from '@/utils/dateUtils';
import { varianceMinutes } from './settlingInVariance';
import type { IndicatorValue } from '@/lib/settlingInIndicator';
import type { ScheduleEntryResponse, TrackingEntryResponse } from '@/types/settlingIn';

/** Tri-state per-day comparison outcome. */
export type DayStatus = 'ok' | 'deviated' | 'missing-actual';

/**
 * A synthetic per-day row overlaying two independently versioned records. Planned
 * cells edit the schedule entry (schedule version); Actual cells edit/create the
 * tracking entry (tracking version) — the two versions are NEVER merged.
 */
export interface DayRow {
  /** The normalized "YYYY-MM-DD" date key — also the table row id. */
  id: string;
  date: string;

  // Schedule (plan) side.
  scheduleId?: string;
  scheduleVersion?: number;
  scheduleEntry?: ScheduleEntryResponse;

  // Tracking (actual) side.
  trackingId?: string;
  trackingVersion?: number;
  trackingEntry?: TrackingEntryResponse;

  // Planned cell fields (schedule stores wall-clock "HH:MM" strings).
  planned_child_present_from?: string;
  planned_child_present_to?: string;
  planned_contact_present_from?: string;
  planned_contact_present_to?: string;
  planned_employee_id?: string;
  planned_contact_id?: string;

  // Actual cell fields (tracking stores TIMESTAMPTZ instants — ISO strings).
  actual_child_present_from?: string;
  actual_child_present_to?: string;
  actual_contact_present_from?: string;
  actual_contact_present_to?: string;

  // Derived.
  weekIndex: number;
  weekdayLabel: string;
  dayStatus: DayStatus;
}

/**
 * Parse a bare "YYYY-MM-DD" (or ISO) date into a local-zone Date without the
 * UTC-midnight day shift that `new Date("YYYY-MM-DD")` causes in negative-offset
 * browsers. Returns undefined when unparseable.
 */
export function parseDayKey(input: string): Date | undefined {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input);
  if (dateOnly) {
    const [, y, m, d] = dateOnly;
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return isValid(date) ? date : undefined;
  }
  const parsed = parseISO(input);
  return isValid(parsed) ? parsed : undefined;
}

/** Map a day-status onto the reused IndicatorValue vocabulary (no settling-* tokens). */
export function dayStatusToIndicatorValue(status: DayStatus): IndicatorValue {
  switch (status) {
    case 'ok':
      return 'good';
    case 'deviated':
      return 'neutral';
    case 'missing-actual':
      return 'unset';
  }
}

interface DeriveDayStatusArgs {
  /** org-zone "YYYY-MM-DD" today; a scheduled day on/before this with no tracking is missing. */
  today: string;
}

/**
 * Derive the tri-state day status:
 *   - `missing-actual`: a scheduled day on/before today with NO tracking entry.
 *   - `deviated`: tracking present and any non-null child-present window variance ≠ 0.
 *   - `ok`: tracking present and on-plan (including an explicit absent-marked day,
 *     whose null child-present window is neither missing nor a deviation).
 */
export function deriveDayStatus(row: DayRow, { today }: DeriveDayStatusArgs): DayStatus {
  const hasSchedule = !!row.scheduleId;
  const hasTracking = !!row.trackingId;

  if (hasSchedule && !hasTracking && row.date <= today) {
    return 'missing-actual';
  }

  if (hasTracking) {
    const fromDelta = varianceMinutes(row.planned_child_present_from, row.actual_child_present_from);
    const toDelta = varianceMinutes(row.planned_child_present_to, row.actual_child_present_to);
    if ((fromDelta !== null && fromDelta !== 0) || (toDelta !== null && toDelta !== 0)) {
      return 'deviated';
    }
  }

  return 'ok';
}

export interface BuildDayRowsOptions {
  /** org-zone "YYYY-MM-DD" used for the missing-actual cutoff. Defaults to now. */
  today?: string;
  /** date-fns locale for the weekday label. */
  locale?: Locale;
}

/**
 * Join schedule ⟕ tracking into one `DayRow` per calendar day, sorted ascending.
 * Mirrors the `scheduleByDate` join in the former TrackingTab, extended to the
 * full union of schedule and tracking dates.
 */
export function buildDayRows(
  scheduleEntries: ScheduleEntryResponse[],
  trackingEntries: TrackingEntryResponse[],
  options: BuildDayRowsOptions = {},
): DayRow[] {
  const today = options.today ?? toOrgDateOnly(new Date().toISOString());

  const scheduleByDate = new Map<string, ScheduleEntryResponse>();
  scheduleEntries.forEach((e) => {
    const key = toOrgDateOnly(e.date);
    if (key) scheduleByDate.set(key, e);
  });

  const trackingByDate = new Map<string, TrackingEntryResponse>();
  trackingEntries.forEach((e) => {
    const key = toOrgDateOnly(e.date);
    if (key) trackingByDate.set(key, e);
  });

  const dateKeys = Array.from(new Set([...scheduleByDate.keys(), ...trackingByDate.keys()])).sort();

  // Group distinct ISO weeks into a stable 1-based sequence (Wk 1, Wk 2, …).
  const weekSequence = new Map<string, number>();
  dateKeys.forEach((key) => {
    const date = parseDayKey(key);
    if (!date) return;
    const weekKey = `${getISOWeekYear(date)}-${getISOWeek(date)}`;
    if (!weekSequence.has(weekKey)) {
      weekSequence.set(weekKey, weekSequence.size + 1);
    }
  });

  return dateKeys.map((key) => {
    const schedule = scheduleByDate.get(key);
    const tracking = trackingByDate.get(key);
    const date = parseDayKey(key);
    const weekKey = date ? `${getISOWeekYear(date)}-${getISOWeek(date)}` : '';

    const row: DayRow = {
      id: key,
      date: key,
      scheduleId: schedule?.id,
      scheduleVersion: schedule?.version,
      scheduleEntry: schedule,
      trackingId: tracking?.id,
      trackingVersion: tracking?.version,
      trackingEntry: tracking,

      planned_child_present_from: schedule?.child_present_from ?? undefined,
      planned_child_present_to: schedule?.child_present_to ?? undefined,
      planned_contact_present_from: schedule?.contact_present_from ?? undefined,
      planned_contact_present_to: schedule?.contact_present_to ?? undefined,
      planned_employee_id: schedule?.employee_id ?? undefined,
      planned_contact_id: schedule?.contact_id ?? undefined,

      actual_child_present_from: tracking?.child_present_from ?? undefined,
      actual_child_present_to: tracking?.child_present_to ?? undefined,
      actual_contact_present_from: tracking?.contact_present_from ?? undefined,
      actual_contact_present_to: tracking?.contact_present_to ?? undefined,

      weekIndex: weekSequence.get(weekKey) ?? 1,
      weekdayLabel: date ? format(date, 'EEE', options.locale ? { locale: options.locale } : undefined) : '',
      dayStatus: 'ok',
    };

    row.dayStatus = deriveDayStatus(row, { today });
    return row;
  });
}

/**
 * Scheduled days on or before today with no tracking entry — the closure
 * checklist's "days missing actuals" count. Shared with `ClosureChecklistCard`.
 */
export function countMissingActuals(
  scheduleEntries: ScheduleEntryResponse[],
  trackingEntries: TrackingEntryResponse[],
  today: string = toOrgDateOnly(new Date().toISOString()),
): number {
  const trackedDates = new Set(trackingEntries.map((e) => toOrgDateOnly(e.date)).filter(Boolean));
  return scheduleEntries.filter((e) => {
    const key = toOrgDateOnly(e.date);
    return !!key && key <= today && !trackedDates.has(key);
  }).length;
}
